import {
  createSlice,
  isPending,
  isFulfilled,
  isRejected,
  type PayloadAction,
  type UnknownAction,
} from '@reduxjs/toolkit';

import type { ValoresPadraoDoAgente, NegociacaoSimulacaoState } from '../types';
import { registrarSimulacaoDoAdmin } from './admin/reducers';
import { registrarAgenteDaSimulacao } from './agente/reducers';
import { registrarAssinaturasSimuladas } from './assinatura/reducers';
import { registrarCicloDaSimulacao } from './ciclo/reducers';
import { registrarEntradasDaConversa } from './entrada/reducers';
import { initialState } from './estadoInicial';
import { registrarEventosDaSimulacao } from './eventos/reducers';
import { registrarLogsDaSimulacao } from './logs/reducers';
import { registrarAvancoDoRelogio } from './relogio/reducers';

const negociacaoSimulacaoSlice = createSlice({
  name: 'negociacaoSimulacao',
  initialState,

  reducers: {
    /** Esconde o que já chegou, sem chamar o core: o polling e a exportação seguem (CA-22). */
    conversaLimpa: (state) => {
      state.limpoAteSeq = state.ultimoSeq;
    },
    /**
     * Padrões do agente preenchem só o que a pessoa ainda não tocou, e não contam como alteração.
     * Os detalhes do agente chegam assíncronos: padrão de um agente que já não é o selecionado é
     * descartado, para nada do anterior chegar ao novo (CA-5).
     */
    valoresPadraoAplicados: (state, action: PayloadAction<ValoresPadraoDoAgente>) => {
      if (action.payload.flowId !== state.formulario.flowId) return;
      for (const [nome, valor] of Object.entries(action.payload.valores)) {
        if (state.formulario.valores[nome] === undefined) state.formulario.valores[nome] = valor;
      }
    },
  },
  extraReducers: (builder) => {
    registrarAgenteDaSimulacao(builder);
    registrarCicloDaSimulacao(builder);
    registrarEntradasDaConversa(builder);
    registrarAvancoDoRelogio(builder);
    registrarEventosDaSimulacao(builder);
    registrarAssinaturasSimuladas(builder);
    registrarSimulacaoDoAdmin(builder);
    registrarLogsDaSimulacao(builder);
  },
});

export const { conversaLimpa, valoresPadraoAplicados } = negociacaoSimulacaoSlice.actions;

/** Uma troca de escritório invalida inclusive respostas sem id da simulação, como acesso e assinatura. */
export const negociacaoSimulacaoReducer = (
  state: NegociacaoSimulacaoState | undefined,
  action: UnknownAction,
): NegociacaoSimulacaoState => {
  if (
    !action.type.startsWith('negociacaoSimulacao/') ||
    !(isPending(action) || isFulfilled(action) || isRejected(action))
  ) {
    return negociacaoSimulacaoSlice.reducer(state, action);
  }
  const atual = state ?? initialState;
  const requestId = action.meta.requestId;
  if (atual.requisicoesDescartadas.includes(requestId)) {
    return {
      ...atual,
      requisicoesDescartadas: atual.requisicoesDescartadas.filter((id) => id !== requestId),
    };
  }
  const proximo = negociacaoSimulacaoSlice.reducer(atual, action);
  return {
    ...proximo,
    requisicoesEmVoo: isPending(action)
      ? [...proximo.requisicoesEmVoo, requestId]
      : proximo.requisicoesEmVoo.filter((id) => id !== requestId),
  };
};
